/* Nordri product cards: the hover on the photo (computer), the swipe and its dots (phone),
   videos that play only while seen, the wishlist heart and the Quick view panel the + opens.
   The wishlist is the same list the product page and the wishlist page use. */
(() => {
  if (customElements.get('nordri-card')) return;

  const WISH_KEY = 'nordri:wishlist';
  const root = () => window.Shopify?.routes?.root || '/';
  const hoverMode = matchMedia('(hover: hover) and (min-width: 750px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  const readWish = () => {
    try {
      return JSON.parse(localStorage.getItem(WISH_KEY) || '[]');
    } catch (e) {
      return [];
    }
  };
  const writeWish = (list) => {
    try {
      localStorage.setItem(WISH_KEY, JSON.stringify(list));
    } catch (e) {
      /* Storage can be refused in private windows. */
    }
    document.dispatchEvent(new CustomEvent('nordri:wishlist-change'));
  };

  let toastEl;
  let toastTimer;
  const toast = (text, link) => {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'nc-toast';
      toastEl.setAttribute('role', 'status');
      document.body.append(toastEl);
    }
    toastEl.textContent = text;
    if (link) {
      const a = document.createElement('a');
      a.href = link.href;
      a.textContent = link.label;
      toastEl.append(a);
    }
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-on'), 2600);
  };

  const money = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  const paintHearts = () => {
    const list = readWish();
    document.querySelectorAll('nordri-card [data-wish], .nqv [data-wish]').forEach((b) => {
      const handle = b.closest('[data-handle]')?.dataset.handle;
      b.setAttribute('aria-pressed', String(list.includes(handle)));
    });
  };

  const toggleWish = (handle, button) => {
    const list = readWish();
    const on = !list.includes(handle);
    writeWish(on ? [...list, handle] : list.filter((h) => h !== handle));
    paintHearts();
    if (button) {
      button.classList.remove('is-pop');
      void button.offsetWidth;
      button.classList.add('is-pop');
    }
    toast(on ? 'Saved to your wishlist' : 'Removed from your wishlist', on ? { href: `${root()}pages/wishlist`, label: 'View' } : null);
  };

  /* Plays a card's video only while it is the slide in view. */
  const videoWatch = new IntersectionObserver(
    (entries) => {
      entries.forEach((en) => {
        const video = en.target;
        if (en.isIntersecting && en.intersectionRatio > 0.6 && !reduced.matches) video.play().catch(() => {});
        else video.pause();
      });
    },
    { threshold: [0, 0.6, 1] }
  );

  const seenWatch = new IntersectionObserver(
    (entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting || hoverMode.matches) return;
        en.target.classList.add('is-seen');
        seenWatch.unobserve(en.target);
      });
    },
    { threshold: 0.35 }
  );

  class NordriCard extends HTMLElement {
    connectedCallback() {
      this.media = this.querySelector('.nc__media');
      this.track = this.querySelector('[data-track]');
      if (!this.media || !this.track) return;

      /* Computer: the hover belongs to the photo, not to the title or price. */
      this.media.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !hoverMode.matches) return;
        this.classList.add('is-h');
        const second = this.track.children[1];
        second?.querySelector?.('video')?.play().catch(() => {});
      });
      this.media.addEventListener('pointerleave', () => {
        this.classList.remove('is-h');
        this.track.children[1]?.querySelector?.('video')?.pause();
      });
      this.media.addEventListener('focusin', () => hoverMode.matches && this.classList.add('is-h'));
      this.media.addEventListener('focusout', (e) => {
        if (!this.media.contains(e.relatedTarget)) this.classList.remove('is-h');
      });

      /* Phone: dots follow the swipe; a tap on the photo opens the piece. */
      const dots = [...this.querySelectorAll('.nc__dots i')];
      this.track.addEventListener(
        'scroll',
        () => {
          const i = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
          dots.forEach((d, k) => d.classList.toggle('is-on', k === i));
        },
        { passive: true }
      );
      this.media.addEventListener('click', (e) => {
        if (e.target.closest('button, a') || hoverMode.matches) return;
        const link = this.querySelector('.nc__link');
        if (link?.href) location.href = link.href;
      });

      this.querySelectorAll('video').forEach((v) => videoWatch.observe(v));
      seenWatch.observe(this);

      this.querySelector('[data-wish]')?.addEventListener('click', (e) => {
        e.preventDefault();
        toggleWish(this.dataset.handle, e.currentTarget);
      });
      this.querySelector('[data-quick]')?.addEventListener('click', (e) => {
        e.preventDefault();
        QuickView.open(this.dataset.handle, e.currentTarget);
      });
      paintHearts();
    }

    disconnectedCallback() {
      this.querySelectorAll('video').forEach((v) => videoWatch.unobserve(v));
      seenWatch.unobserve(this);
    }
  }
  customElements.define('nordri-card', NordriCard);

  /* ---------- Quick view: one panel, filled from the product's own data when it opens ---------- */
  const ICON = {
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>',
    heart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true"><path d="M12 20s-7-4.4-9.2-9A5 5 0 0 1 12 6a5 5 0 0 1 9.2 5c-2.2 4.6-9.2 9-9.2 9Z"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>'
  };
  const sized = (src, width) => {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const QuickView = {
    dialog: null,
    product: null,
    chosen: [],
    opener: null,
    cache: {},

    build() {
      const d = document.createElement('dialog');
      d.className = 'nqv';
      d.setAttribute('aria-label', 'Quick view');
      d.innerHTML = `<button class="nqv__close" type="button" aria-label="Close">${ICON.close}</button><div class="nqv__content"></div>`;
      d.querySelector('.nqv__close').addEventListener('click', () => d.close());
      d.addEventListener('click', (e) => {
        if (e.target === d) d.close();
      });
      d.addEventListener('close', () => this.opener?.focus({ preventScroll: true }));
      document.body.append(d);
      this.dialog = d;
    },

    async open(handle, opener) {
      if (!this.dialog) this.build();
      this.opener = opener;
      const content = this.dialog.querySelector('.nqv__content');
      content.innerHTML = '<div class="nqv__loading">Loading…</div>';
      if (!this.dialog.open) this.dialog.showModal();
      try {
        this.product = this.cache[handle] || (this.cache[handle] = await (await fetch(`${root()}products/${handle}.js`)).json());
      } catch (e) {
        content.innerHTML = '<div class="nqv__loading">That did not load. Please try again.</div>';
        return;
      }
      const p = this.product;
      const first = p.variants.find((v) => v.available) || p.variants[0];
      this.chosen = [...first.options];
      const images = (p.media || []).filter((m) => m.media_type === 'image' || m.preview_image).slice(0, 8);
      const options = p.options
        .map((o, i) => {
          if (p.variants.length === 1) return '';
          return `<fieldset class="nqv__option"><legend>${esc(o.name)}</legend><div class="nqv__values">${o.values
            .map((val) => `<button type="button" data-option="${i}" data-value="${esc(val)}">${esc(val)}</button>`)
            .join('')}</div></fieldset>`;
        })
        .join('');
      content.innerHTML = `<div class="nqv__body" data-handle="${esc(p.handle)}">
          <div class="nqv__media">
            <div class="nqv__big"><img src="${esc(sized(images[0]?.preview_image?.src || images[0]?.src || p.featured_image, 800))}" alt="${esc(p.title)}" width="800" height="800"></div>
            ${images.length > 1 ? `<div class="nqv__thumbs">${images.map((m, i) => `<button type="button" aria-pressed="${i === 0}" aria-label="Photo ${i + 1}" data-src="${esc(sized(m.preview_image?.src || m.src, 800))}"><img src="${esc(sized(m.preview_image?.src || m.src, 120))}" alt="" width="56" height="56" loading="lazy"></button>`).join('')}</div>` : ''}
          </div>
          <div class="nqv__info">
            <h2 class="nqv__title">${esc(p.title)}</h2>
            <div class="nqv__price" data-price></div>
            ${options}
            <div class="nqv__actions">
              <button class="button-secondary" type="button" data-add>Add to cart</button>
              <button class="nqv__wish" type="button" aria-pressed="false" aria-label="Save to wishlist" data-wish>${ICON.heart}</button>
            </div>
            <p class="nqv__error" data-error hidden></p>
            <a class="nqv__more" href="${esc(p.url)}">View full details${ICON.arrow}</a>
          </div>
        </div>`;

      content.querySelectorAll('[data-option]').forEach((b) =>
        b.addEventListener('click', () => {
          this.chosen[Number(b.dataset.option)] = b.dataset.value;
          this.paint();
        })
      );
      content.querySelectorAll('.nqv__thumbs button').forEach((b) =>
        b.addEventListener('click', () => {
          content.querySelector('.nqv__big img').src = b.dataset.src;
          content.querySelectorAll('.nqv__thumbs button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        })
      );
      /* The big photo changes with a swipe (finger, mouse drag or touchpad) as well as the thumbnails. */
      const big = content.querySelector('.nqv__big');
      const thumbs = [...content.querySelectorAll('.nqv__thumbs button')];
      if (big && thumbs.length > 1) {
        const go = (d) => {
          const i = Math.max(0, thumbs.findIndex((x) => x.getAttribute('aria-pressed') === 'true'));
          const t = thumbs[(i + d + thumbs.length) % thumbs.length];
          t.click();
          t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
            big.querySelector('img')?.animate([{ opacity: 0.2, transform: `translateX(${d * 50}px)` }, { opacity: 1, transform: 'none' }], { duration: 320, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
          }
        };
        let sx = null;
        let sy = 0;
        big.style.touchAction = 'pan-y pinch-zoom';
        big.addEventListener('dragstart', (e) => e.preventDefault());
        big.addEventListener('pointerdown', (e) => {
          sx = e.clientX;
          sy = e.clientY;
        });
        big.addEventListener('pointerup', (e) => {
          if (sx === null) return;
          const d = e.clientX - sx;
          if (Math.abs(d) > 40 && Math.abs(d) > Math.abs(e.clientY - sy)) go(d < 0 ? 1 : -1);
          sx = null;
        });
        big.addEventListener('pointercancel', () => (sx = null));
        let acc = 0;
        let lock = 0;
        big.addEventListener(
          'wheel',
          (e) => {
            if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
            e.preventDefault();
            if (performance.now() < lock) return;
            acc += e.deltaX;
            if (Math.abs(acc) > 50) {
              go(acc > 0 ? 1 : -1);
              acc = 0;
              lock = performance.now() + 450;
            }
          },
          { passive: false }
        );
      }
      content.querySelector('[data-wish]').addEventListener('click', (e) => toggleWish(p.handle, e.currentTarget));
      content.querySelector('[data-add]').addEventListener('click', () => this.add());
      this.paint();
      paintHearts();
    },

    variant() {
      return this.product.variants.find((v) => v.options.every((o, i) => o === this.chosen[i]));
    },

    paint() {
      const p = this.product;
      const v = this.variant();
      const c = this.dialog;
      c.querySelectorAll('[data-option]').forEach((b) => {
        const i = Number(b.dataset.option);
        b.setAttribute('aria-pressed', String(this.chosen[i] === b.dataset.value));
        const test = [...this.chosen];
        test[i] = b.dataset.value;
        const match = p.variants.find((x) => x.options.every((o, k) => o === test[k]));
        b.toggleAttribute('data-out', !match || !match.available);
      });
      const price = c.querySelector('[data-price]');
      const add = c.querySelector('[data-add]');
      if (!v) {
        price.innerHTML = '';
        add.disabled = true;
        add.textContent = 'Unavailable';
        return;
      }
      const sale = v.compare_at_price > v.price;
      price.innerHTML = `<b>${money(v.price)}</b>${sale ? `<s>${money(v.compare_at_price)}</s><span>Save ${Math.round((1 - v.price / v.compare_at_price) * 100)}%</span>` : ''}`;
      add.disabled = !v.available;
      add.textContent = v.available ? `Add to cart · ${money(v.price)}` : 'Sold out';
      if (v.featured_image?.src) c.querySelector('.nqv__big img').src = sized(v.featured_image.src, 800);
    },

    async add() {
      const v = this.variant();
      if (!v || !v.available) return;
      const button = this.dialog.querySelector('[data-add]');
      const error = this.dialog.querySelector('[data-error]');
      button.disabled = true;
      error.hidden = true;
      let events = null;
      try {
        events = await import('@shopify/events');
      } catch (e) {
        events = null;
      }
      const deferred = events?.CartLinesUpdateEvent?.createPromise?.();
      if (deferred) {
        document.dispatchEvent(
          new events.CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(v.id), quantity: 1 }], promise: deferred.promise })
        );
      }
      try {
        const sections = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
        const r = await fetch(`${root()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }], sections: sections.join(',') })
        });
        const j = await r.json();
        if (j.status) throw new Error(j.description || j.message);
        const cart = await (await fetch(`${root()}cart.js`)).json();
        if (deferred) {
          deferred.resolve({
            cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, source: 'nordri-card', itemCount: 1, productId: String(this.product.id), sections: j.sections, didError: false }
          });
        }
        this.dialog.close();
        toast('Added to your cart', { href: `${root()}cart`, label: 'View cart' });
      } catch (err) {
        deferred?.reject?.(err);
        error.textContent = err.message || 'That could not be added. Please try again.';
        error.hidden = false;
        button.disabled = false;
      }
    }
  };

  addEventListener('storage', (e) => e.key === WISH_KEY && paintHearts());
})();
